import React, { useCallback, useMemo } from 'react';

import { Checkbox, TableCell, TableHead, TableRow } from '@mui/material';

import { classes } from './styles';
import { handleScrollableEnter, handleScrollableExit, handleSelectAllOnPage } from './tableHelper';
import TooltipTableCell from './TooltipTableCell';
import { updateUiSettings } from '../../helpers/uiPreferencesHelpers';

import type { SxProps } from '@mui/material';
import type { SystemCssProperties } from '@mui/system';
import type { Dispatch, MutableRefObject, RefObject } from 'react';

import type {
  GenericTableData,
  TablePaginationAction,
  TableScrollAction,
  TableSelectionAction,
  TPaginationState,
  TSortableColumn,
  TTableState
} from './types';

// Spreading SxProps only keeps plain style objects; callback and array styles spread to nothing.
const styleObject = (style: SxProps): SystemCssProperties =>
  typeof style === 'object' && style !== null && !Array.isArray(style)
    ? (style as SystemCssProperties)
    : {};

interface IGenericTableHeadProps<T> {
  tableState: TTableState<T, TSortableColumn[]>;
  paginationState: TPaginationState<string>;
  dispatch: Dispatch<TableSelectionAction<T> | TablePaginationAction<string> | TableScrollAction>;
  items: T[];
  titleCellRef: RefObject<HTMLTableCellElement | null>;
  actionCellRef: RefObject<HTMLTableCellElement | null>;
  headerStyle: (id: string, tableHovered: boolean) => SxProps;
  titleCellId?: string;
  userPreferenceSource?: string;
  hasActionItems?: boolean;
  hasStickyColumnBeforeActions?: boolean;
  allowMultipleSelection?: boolean;
  customRowStyles?: Record<string, SxProps>;
  tableRef: RefObject<HTMLDivElement>;
  hoverTimeoutRef: MutableRefObject<NodeJS.Timeout | null>;
  isHoveringRef: MutableRefObject<boolean>;
}

const GenericTableHead = <GenericTableHeadProps extends GenericTableData>({
  tableState,
  paginationState,
  dispatch,
  items,
  titleCellRef,
  actionCellRef,
  headerStyle,
  titleCellId = 'name',
  userPreferenceSource,
  hasActionItems = true,
  hasStickyColumnBeforeActions,
  allowMultipleSelection = true,
  customRowStyles,
  tableRef,
  hoverTimeoutRef,
  isHoveringRef
}: IGenericTableHeadProps<GenericTableHeadProps>) => {
  const localHoverTimeoutRef = hoverTimeoutRef;
  const localIsHoveringRef = isHoveringRef;
  const selectAllIsIndeterminate = useMemo(
    () => items.map((item) => item.id).some((id) => tableState.selectedItemIds.includes(id)),
    [items, tableState.selectedItemIds]
  );
  // An empty page has nothing selected; `every` alone is true for no rows.
  const selectAllIsChecked = useMemo(
    () =>
      items.length > 0 &&
      items.map((item) => item.id).every((id) => tableState.selectedItemIds.includes(id)),
    [items, tableState.selectedItemIds]
  );

  const handleSortChange = (id: string) => {
    const sortOrder =
      paginationState.sort === id && paginationState.sort_order === 'asc' ? 'desc' : 'asc';
    dispatch({ type: 'SET_SORT', payload: { sortBy: id, sortOrder } });
    if (userPreferenceSource) {
      updateUiSettings({
        source: userPreferenceSource,
        sorting: {
          sortBy: id,
          sortOrder
        }
      });
    }
  };

  const clearHoverTimeout = useCallback(() => {
    if (localHoverTimeoutRef.current) {
      clearTimeout(localHoverTimeoutRef.current);
      localHoverTimeoutRef.current = null;
    }
  }, [localHoverTimeoutRef]);

  const handleRowMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement>) => {
      if (!titleCellRef.current || !actionCellRef.current) return;

      const titleRect = titleCellRef.current.getBoundingClientRect();
      const actionRect = actionCellRef.current.getBoundingClientRect();
      const mouseX = event.clientX;

      if (mouseX > titleRect.right && mouseX < actionRect.left) {
        clearHoverTimeout();
        localIsHoveringRef.current = true;
        handleScrollableEnter(tableRef, dispatch);
      } else {
        localIsHoveringRef.current = false;
        handleScrollableExit(dispatch);
      }
    },
    [tableRef, dispatch, titleCellRef, actionCellRef, clearHoverTimeout, localIsHoveringRef]
  );

  const handleRowMouseLeave = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement>) => {
      const relatedTarget = event.relatedTarget as HTMLElement;

      // Check if mouse is leaving to scroll arrows
      const isArrowElement =
        relatedTarget?.id === 'left-arrow' || relatedTarget?.id === 'right-arrow';
      const isInsideArrow =
        typeof relatedTarget?.closest === 'function' &&
        (relatedTarget.closest('#left-arrow') !== null ||
          relatedTarget.closest('#right-arrow') !== null);

      if (relatedTarget && (isArrowElement || isInsideArrow)) {
        return;
      }

      localIsHoveringRef.current = false;
      clearHoverTimeout();
      localHoverTimeoutRef.current = setTimeout(() => {
        if (!localIsHoveringRef.current) {
          handleScrollableExit(dispatch);
        }
        localHoverTimeoutRef.current = null;
      }, 400);
    },
    [dispatch, clearHoverTimeout, localHoverTimeoutRef, localIsHoveringRef]
  );

  return (
    <TableHead>
      <TableRow
        onMouseEnter={handleRowMouseEnter}
        onMouseLeave={handleRowMouseLeave}
        sx={Object.fromEntries(
          Object.entries(customRowStyles ?? {}).filter(([key]) => !key.includes(':hover'))
        )}
      >
        {allowMultipleSelection ? (
          <TableCell sx={headerStyle('checkbox', tableState.tableHovered)}>
            <Checkbox
              checked={selectAllIsChecked}
              id="select-all-checkbox"
              indeterminate={selectAllIsIndeterminate ? !selectAllIsChecked : null}
              onChange={(e) => handleSelectAllOnPage(e, items, dispatch)}
              sx={classes.checkbox}
              slotProps={{
                input: {
                  'aria-label': 'select all items on page'
                }
              }}
            />
          </TableCell>
        ) : null}
        {tableState.columns.map((header) => {
          const baseStyles = headerStyle(header.id, tableState.tableHovered);
          const baseStylesObj = styleObject(baseStyles);
          const baseLeft = baseStylesObj.left;
          const hasLeft =
            typeof baseLeft === 'number'
              ? baseLeft !== 0
              : typeof baseLeft === 'string' && baseLeft !== '';
          const isSticky = allowMultipleSelection && baseStylesObj.position === 'sticky' && hasLeft;
          let adjustedStyles = baseStyles;
          if (isSticky && (typeof baseLeft === 'number' || typeof baseLeft === 'string')) {
            const adjustedLeft =
              typeof baseLeft === 'number' ? baseLeft + 58 : `calc(${baseLeft} + 58px)`;
            adjustedStyles = { ...baseStylesObj, left: adjustedLeft };
          }

          return (
            <TooltipTableCell
              key={header.id}
              ref={header.id === titleCellId ? titleCellRef : undefined}
              header={header}
              onSortChange={handleSortChange}
              paginationState={paginationState}
              sx={adjustedStyles}
            />
          );
        })}
        <TableCell
          ref={actionCellRef}
          sx={{
            ...styleObject(headerStyle('actions', tableState.tableHovered)),
            ...(hasStickyColumnBeforeActions &&
              tableState.tableHovered && { '&&::after': { opacity: 0 } }),
            ...(!hasActionItems && { minWidth: '20px', width: '20px' })
          }}
        />
      </TableRow>
    </TableHead>
  );
};

export default GenericTableHead;
